/**
 * Aide contextuelle — Dashboard principal
 */

const dashboardHelp = {
  '/dashboard': {
    title: 'Tableau de bord METERIS',
    user: [
      {
        heading: 'A quoi sert cette page ?',
        content: `C'est votre page d'accueil apres connexion. Elle offre un acces rapide aux outils les plus utilises au quotidien : sites internes, bons d'intervention, reception client et base de connaissances.`,
      },
      {
        heading: 'Les 4 raccourcis',
        content: `**Sites** — Acces aux portails internes (ESET, Acronis, Odoo, Yeastar, etc.) et aux sites publics du groupe (meteris.fr, atelier.meteris.fr, boutique, MCF...).\n**Bon d'Intervention** — Saisie terrain avec signature numerique et export PDF.\n**Reception Client** — Enregistrement des interventions en atelier.\n**Base de Connaissances** — Articles et procedures techniques.`,
      },
      {
        heading: 'Filtrage par entite',
        content: `Utilisez le **selecteur d'entite** dans la sidebar pour filtrer les donnees par societe. Le filtre s'applique a toutes les pages du portail. Cliquez sur "Toutes les entites" pour revenir a la vue globale.`,
      },
      {
        heading: 'Recherche unifiee',
        content: `La barre de recherche en haut de la sidebar (\`Ctrl+K\`) recherche simultanement dans les **menus**, les tickets, les utilisateurs, les societes, les **postes du parc** et les **articles de la base de connaissances**. Les menus apparaissent des le premier caractere ; les autres a partir de 2 caracteres. Naviguez dans les resultats avec les **fleches** du clavier et validez avec **Entree**.\n\nUn poste ouvre sa fiche (\`/parc/ordinateurs/…\`), un article ouvre sa page KB. Vous ne voyez que ce que vos droits autorisent (\`parc.view\`, \`kb.view\`).`,
      },
    ],
    admin: [
      {
        heading: 'Architecture globale',
        content: `Le portail est structure en modules independants : Ticketing (CRUD tickets ITIL), Parc (ESET Connect + TeamViewer + Acronis), Gestion (societes/utilisateurs/groupes/domaines), KB (articles), Outils (licences ESET, BI, reception, messagerie vocale, documents), Incidents (reponse a incidents multi-journal), Vault (coffre-fort chiffre AES-256-GCM), Rapports (KPI par client), Assistant (bot Telegram).`,
      },
      {
        heading: 'Modele de permissions',
        content: `**Roles** : superadmin, admin_n2, admin_n1, tech_n2, tech_n1, client (+ roles externes : utilisateur, superviseur, comptable, directeur).\n\nChaque route requiert une permission specifique (ex: \`ticketing.view\`, \`vault.view\`, \`incidents.manage\`). Le superadmin a acces a tout.\n\n**Filtrage entite** : Les clients ne voient que les donnees de leur societe. Les techniciens voient tout sauf si le filtre entite est actif.`,
      },
      {
        heading: 'Authentification',
        content: `**SSO** : Microsoft 365 et Google (OAuth2). **Fallback local** : accessible via \`?debug=1\` sur la page login.\n\nSession geree par cookie JWT (\`meteris_portal_session\`). TTL configurable. Impersonation disponible pour les superadmins.`,
      },
      {
        heading: 'Notifications',
        content: `La cloche de notification (visible si permission \`eset.manage\`) agrege les alertes ESET (devices critiques), licences ESET (expirations trials) et Acronis (alertes backup). Poll toutes les 30 secondes. Cliquables vers la page source.`,
      },
    ],
  },

  '/ma-journee': {
    title: 'Ma journée',
    user: [
      {
        heading: 'À quoi sert cette page ?',
        content: `Ce qui t'attend aujourd'hui, par priorité, notamment : tickets **prioritaires**, tickets **non pris**, **SLA dépassé**, **attentes qui stagnent**, **tâches en retard**, **questions internes**. Un clic sur une ligne ouvre ce qu'elle désigne.\n\n**Moi** — ce qui te concerne, toi.\n**Mon groupe** — ce qui attend ton groupe (c'est là que sont les tickets **non pris**). Une urgence de groupe est rappelée en tête de « Moi ».`,
      },
      {
        heading: 'Découvrir le portail',
        content: `Pour un compte interne, le bouton **?** propose « **Premiers pas** » : une visite guidée d'une dizaine de minutes, que tu peux arrêter et reprendre (« Reprendre les Premiers pas »).`,
      },
    ],
    admin: [
      {
        heading: 'Source et réglages',
        content: `\`GET /api/me/briefing\` (briefing v2) : le serveur calcule chaque société avec les droits de CETTE société ; la page ne filtre aucun droit. La sélection (\`vue\`, \`zone\`, \`societe\`) vit dans l'URL — la même que les liens du mail du matin. Un clic sur l'en-tête d'une rubrique la replie ou la déplie.`,
      },
    ],
  },

  '/user/preferences': {
    title: 'Mes preferences',
    user: [
      {
        heading: 'A quoi sert cette page ?',
        content: `Personnalisez l'interface du portail selon vos habitudes de travail : reordonnez les menus de la sidebar, renommez les sections, choisissez la vue par defaut des tickets (liste ou kanban).`,
      },
      {
        heading: 'Reordonner les menus',
        content: `**Glissez les sections** (ex: Ticketing, Parc) pour changer leur position dans la sidebar. Depliez une section pour reordonner ou deplacer ses sous-elements entre sections.`,
      },
      {
        heading: 'Renommer',
        content: `**Double-cliquez** sur le nom d'une section ou d'un element pour le renommer. Le nom personnalise apparaitra dans la sidebar. Reinitialiser la sidebar supprime tous les noms personnalises.`,
      },
      {
        heading: 'Recherche et filtres',
        content: `Utilisez le champ de recherche pour retrouver rapidement un element. Les puces de section permettent de filtrer l'affichage par categorie.`,
      },
      {
        heading: 'Vue par defaut des tickets',
        content: `Choisissez entre la vue **Liste** (tableau classique) et la vue **Kanban** (colonnes par statut, drag & drop). Ce choix s'applique a chaque visite de la page Tickets.`,
      },
    ],
    admin: [
      {
        heading: 'Stockage des preferences',
        content: `\`PUT /api/user/preferences\` — Sauvegarde partielle (merge). Champs : \`sidebar_order\` (array d'IDs), \`sidebar_items\` (map section→paths), \`custom_labels\` (map path→label), \`column_orders\` (map table→colonnes), \`tickets_view\` (list/kanban).\n\nCache en memoire cote frontend (hook \`usePreferences\`) avec mise a jour optimiste.`,
      },
    ],
  },
  '/notifications': {
    title: 'Centre de notifications',
    user: [
      {
        heading: 'A quoi sert cette page ?',
        content: `Toutes vos notifications en attente, au-dela des dernieres visibles dans la **cloche**. Filtrez par categorie ou par texte, triez par date ou par criticite, et traitez-les **en masse** : cochez des lignes puis **Reporter a demain 8 h** ou **Ignorer**.`,
      },
      {
        heading: 'Reporter ou ignorer',
        content: `**Reporter a demain 8 h** fait disparaitre la notification jusqu'au lendemain matin, ou elle revient dans la cloche. **Ignorer** la ferme definitivement. **Tout ignorer** ne touche jamais aux notifications **critiques** : celles-la se traitent une par une, c'est voulu.`,
      },
      {
        heading: 'Pourquoi une notification revient ?',
        content: `Une notification reportee revient a l'heure dite. Une notification **liee a un ticket** (SLA, reponse client) revient tant que la cause persiste : c'est le ticket qu'il faut traiter, pas la notification.`,
      },
      {
        heading: 'Le filtre « Pour moi » (dirigeant)',
        content: `Pour le dirigeant seul, la cloche et cette page ne montrent par defaut que ce qui est **pour lui** : ce qui lui est adresse a lui seul, ce qui porte sur un ticket qui lui est assigne, et les alertes **Securite et systeme**. Le reste est compte : **Tout voir (N)** l'affiche le temps de la visite. Dans la vue « Pour moi », **Tout ignorer** n'ignore que ce qui est affiche. La case en haut de page coupe ou remet le filtre. Les autres comptes ne voient aucune difference.`,
      },
    ],
    admin: [
      {
        heading: 'Endpoints API',
        content: `\`GET /api/notifications\` (liste, filtres) · \`POST /api/notifications/mark-read\` · \`POST /api/notifications/snooze\` (\`until\` = lendemain 08:00 local) · \`POST /api/notifications/dismiss-bulk\` (\`{category}\` ou \`{dismiss_all: true, exclude_critical: true}\`). Filtre « Pour moi » : \`?pour_moi=1\` sur la liste et sur \`/count\` (applique seulement au dirigeant dont le reglage est actif ; reponse \`pour_moi: {actif, hors_filtre}\`), reglage \`GET/PUT /api/me/pilotage/cloche\`.\n\nLa page et la cloche partagent **un seul flux SSE** par onglet (\`lib/eventStream.js\`) : ouvrir le centre n'ouvre pas une seconde connexion \`/api/events/stream\`.`,
      },
    ],
  },
};

export default dashboardHelp;
